import { isAuthenticatedUser } from 'js/lib/user';

import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import { PERSONALIZATION_TOP_SECTION } from 'bundles/personalization-settings/constants';
import { useIsEditGoalRedirectEnabled } from 'bundles/personalization-settings/featureFlags';

/**
 * Destination for the site-wide "Edit goal" / edit-profile entry points.
 *
 * Historically these CTAs deep-link into the onboarding stepper (via
 * {@link useBuildOnboardingUrl}). When the edit-goal-redirect-layer routes here,
 * we instead route the learner to the Personalization tab on Account Settings,
 * where they can edit the same Learner Object attributes (goal, role, skills,
 * education) without re-running onboarding (LO-4742). Redirecting also
 * requires the learner to be authenticated, since the Personalization tab
 * lives behind Account Settings auth and unauthenticated learners must go
 * through onboarding regardless of the redirect layer's value — so the layer
 * read (and its impression) is skipped entirely when unauthenticated.
 *
 * Takes the same arguments as {@link useBuildOnboardingUrl} and returns the same
 * `{ url, loading }` shape, so call sites swap the hook in place.
 */
const PERSONALIZATION_SETTINGS_URL = `/account-settings#${PERSONALIZATION_TOP_SECTION}`;

const useEditGoalUrl = (args: Parameters<typeof useBuildOnboardingUrl>[0]): { url: string; loading: boolean } => {
  // Skip the impression call when not authenticated since we can't redirect to account-settings
  const isAuthenticated = isAuthenticatedUser();
  const isEditGoalRedirectEnabled = useIsEditGoalRedirectEnabled({ skip: !isAuthenticated });
  // Always call the onboarding builder so hook order is stable and we keep the
  // exact gate-off behavior; only the destination changes when the gate is on.
  const { onboardingUrl, loading } = useBuildOnboardingUrl(args);

  if (isEditGoalRedirectEnabled && isAuthenticated) {
    return { url: PERSONALIZATION_SETTINGS_URL, loading: false };
  }

  return { url: onboardingUrl, loading };
};

export default useEditGoalUrl;
